import React, { useState } from "react";
import { connect } from "react-redux";
import { multilanguage } from "redux-multilanguage";
import { getChildNodes } from "../../../utils/nodes";
import { getImageLink } from "../../../utils/images";
import { decodeText } from "../../../utils/richText";

// Same type labels as getAssetType in demo-ftl-base's assets.ftl, so both renderers agree.
const TYPES = [
    [/^image\//, "Image"],
    [/^video\//, "Video"],
    [/^audio\//, "Audio"],
    [/^application\/pdf$/, "PDF"],
    [/(ms-excel|spreadsheet|text\/csv)/, "Spreadsheet"],
    [/(ms-powerpoint|presentation)/, "Presentation"],
    [/(zip|rar|7z|gzip|tar)/, "Archive"],
    [/(msword|wordprocessing|opendocument\.text|^text\/)/, "Document"],
];

// Folder children arrive without binary metadata, so their type comes from the file extension.
const EXTENSION_TYPES = {
    pdf: "PDF", jpg: "Image", jpeg: "Image", png: "Image", svg: "Image", gif: "Image", webp: "Image",
    mp4: "Video", mov: "Video", webm: "Video", mp3: "Audio", wav: "Audio",
    xls: "Spreadsheet", xlsx: "Spreadsheet", csv: "Spreadsheet", ppt: "Presentation", pptx: "Presentation",
    zip: "Archive", gz: "Archive", doc: "Document", docx: "Document", txt: "Document",
};

const getType = (mimeType = "") => TYPES.find(([pattern]) => pattern.test(mimeType))?.[1] || "File";
const getTypeFromName = (name = "") => EXTENSION_TYPES[name.split(".").pop().toLowerCase()] || "File";

// Bootstrap's pagination colours point at the shop's brand palette (set at runtime by
// utils/brandTheme.js) instead of Bootstrap blue, with the flone purple as fallback.
const paginationStyle = {
    "--bs-pagination-color": "var(--brand-500, #a749ff)",
    "--bs-pagination-hover-color": "var(--brand-700, #a749ff)",
    "--bs-pagination-focus-color": "var(--brand-700, #a749ff)",
    "--bs-pagination-focus-box-shadow": "0 0 0 0.25rem var(--brand-100, #f0e0ff)",
    "--bs-pagination-active-bg": "var(--brand-500, #a749ff)",
    "--bs-pagination-active-border-color": "var(--brand-500, #a749ff)",
};

// Flex-centred square buttons with lining figures: the brand fonts use old-style numerals,
// whose short and descending digits otherwise look pushed down inside the button.
const pageLinkStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "2rem",
    height: "2rem",
    padding: "0 0.5rem",
    lineHeight: 1,
    fontVariantNumeric: "lining-nums tabular-nums",
};

const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
};

const toFolderDownload = (asset) => ({
    key: asset["@id"],
    label: asset.name || asset["@name"],
    link: getImageLink(`/dam/jcr:${asset["@id"]}/${asset["@name"]}`),
    type: getTypeFromName(asset["@name"]),
});

// Assets of a folder in Assets app order, then those of its subfolders, like the FreeMarker variant.
const getFolderAssets = (folder, includeSubfolders) => {
    const children = getChildNodes(folder);
    const assets = children.filter((node) => node["@nodeType"] === "mgnl:asset");
    const subfolders = includeSubfolders ? children.filter((node) => node["@nodeType"] === "mgnl:folder") : [];
    return [...assets, ...subfolders.flatMap((subfolder) => getFolderAssets(subfolder, true))];
};

// A picked asset is resolved by the assetReferenceResolver (link + metadata); a DAM folder
// only lists its children as plain nodes, so their link is built from id and name.
const getDownloads = (source = {}) => {
    if (source.field === "folder") {
        const includeSubfolders = source.includeSubfolders === undefined || source.includeSubfolders === true || source.includeSubfolders === "true";
        return getFolderAssets(source.downloadFolder, includeSubfolders).map(toFolderDownload);
    }

    return getChildNodes(source.assets)
        .filter(({ downloadAsset }) => downloadAsset && typeof downloadAsset === "object")
        .map(({ downloadAsset: asset, label }) => {
            const metadata = asset.metadata || {};
            return {
                key: asset["@id"],
                // Same fallback as the FreeMarker variant: title, else the file name without extension.
                label: label || metadata.title || (metadata.fileName || asset["@name"] || "").replace(/\.[^.]+$/, ""),
                link: getImageLink(asset),
                type: getType(metadata.mimeType),
                fileSize: metadata.fileSize,
            };
        });
};

// Compact list of downloadable assets for Grid columns, the headless sibling of
// demo-ftl-base's Downloads component.
const Downloads = ({ strings, title, source, itemsPerPage }) => {
    const [page, setPage] = useState(1);
    const allDownloads = getDownloads(source).filter(({ link }) => link);
    if (!title && !allDownloads.length) return null;

    const perPage = parseInt(itemsPerPage, 10) > 0 ? parseInt(itemsPerPage, 10) : 10;
    const pagesCount = Math.ceil(allDownloads.length / perPage);
    const currentPage = Math.min(page, pagesCount || 1);
    const downloads = allDownloads.slice((currentPage - 1) * perPage, currentPage * perPage);

    return (
        <div>
            {title && <h3 className="h5 mb-3">{decodeText(title)}</h3>}
            <ul className="list-group">
                {downloads.map(({ key, label, link, type, fileSize }) => (
                    <li key={key} className="list-group-item p-0">
                        <a href={link} download className="d-flex align-items-center gap-3 p-3 text-decoration-none text-reset">
                            <i className="fa fa-file-o fa-lg text-muted" aria-hidden="true" />
                            <span className="flex-grow-1 text-truncate">
                                <span className="d-block fw-semibold text-truncate">{decodeText(label)}</span>
                                <small className="text-muted">
                                    {type}
                                    {fileSize ? ` · ${formatFileSize(fileSize)}` : ""}
                                </small>
                            </span>
                            <span className="small fw-semibold text-nowrap">
                                {strings["downloads.cta"]} <i className="fa fa-download" aria-hidden="true" />
                            </span>
                        </a>
                    </li>
                ))}
            </ul>
            {pagesCount > 1 && (
                <nav className="mt-3" aria-label={title ? decodeText(title) : "Downloads"}>
                    <ul className="pagination pagination-sm justify-content-center mb-0" style={paginationStyle}>
                        {Array.from({ length: pagesCount }, (_, i) => i + 1).map((p) => (
                            <li key={p} className={`page-item${p === currentPage ? " active" : ""}`}>
                                <button type="button" className="page-link" style={pageLinkStyle} onClick={() => setPage(p)} aria-current={p === currentPage ? "page" : undefined}>
                                    {p}
                                </button>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
        </div>
    );
};

export default connect()(multilanguage(Downloads));